import { useId } from "react";
import { Bell, Mail, Send, Smartphone } from "lucide-react";
import { cn } from "@/lib/cn";

/** Simplified brand marks (inline SVG — no external assets). */
export function MetaLogo({ className }: { className?: string }) {
  // Official Meta symbol (blue gradient); ids are per-instance so repeated logos don't share gradients.
  const id = useId().replace(/:/g, "");
  return (
    <svg viewBox="0 0 287.56 191" className={cn("h-3.5 w-5", className)} aria-label="Meta" role="img">
      <defs>
        <linearGradient id={`meta-a-${id}`} x1="61" y1="117" x2="259" y2="127" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#0064E1" />
          <stop offset=".4" stopColor="#0064E1" />
          <stop offset=".83" stopColor="#0073EE" />
          <stop offset="1" stopColor="#0082FB" />
        </linearGradient>
        <linearGradient id={`meta-b-${id}`} x1="45" y1="139" x2="45" y2="66" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#0082FB" />
          <stop offset="1" stopColor="#0064E0" />
        </linearGradient>
      </defs>
      <path
        fill="#0081FB"
        d="m31.06 125.96c0 10.98 2.41 19.41 5.56 24.51 4.13 6.68 10.29 9.51 16.57 9.51 8.1 0 15.51-2.01 29.79-21.76 11.44-15.83 24.92-38.05 33.99-51.98l15.36-23.6c10.67-16.39 23.02-34.61 37.18-46.96 11.56-10.08 24.03-15.68 36.58-15.68 21.07 0 41.14 12.21 56.5 35.11 16.81 25.08 24.97 56.67 24.97 89.27 0 19.38-3.82 33.62-10.32 44.87-6.28 10.88-18.52 21.75-39.11 21.75l0-31.02c17.63 0 22.03-16.2 22.03-34.74 0-26.42-6.16-55.74-19.73-76.69-9.63-14.86-22.11-23.94-35.84-23.94-14.85 0-26.8 11.2-40.23 31.17-7.14 10.61-14.47 23.54-22.7 38.13l-9.06 16.05c-18.2 32.27-22.81 39.62-31.91 51.75-15.95 21.24-29.57 29.29-47.5 29.29-21.27 0-34.72-9.21-43.05-23.09-6.8-11.31-10.14-26.15-10.14-43.06z"
      />
      <path
        fill={`url(#meta-a-${id})`}
        d="m24.49 37.3c14.24-21.95 34.79-37.3 58.36-37.3 13.65 0 27.22 4.04 41.39 15.61 15.5 12.65 32.02 33.48 52.63 67.81l7.39 12.32c17.84 29.72 27.99 45.01 33.93 52.22 7.64 9.26 12.99 12.02 19.94 12.02 17.63 0 22.03-16.2 22.03-34.74l27.4-.86c0 19.38-3.82 33.62-10.32 44.87-6.28 10.88-18.52 21.75-39.11 21.75-12.8 0-24.14-2.78-36.68-14.61-9.64-9.08-20.91-25.21-29.58-39.71l-25.79-43.08c-12.94-21.62-24.81-37.74-31.68-45.04-7.39-7.85-16.89-17.33-32.05-17.33-12.27 0-22.69 8.61-31.41 21.78z"
      />
      <path
        fill={`url(#meta-b-${id})`}
        d="m82.35 31.23c-12.27 0-22.69 8.61-31.41 21.78-12.33 18.61-19.88 46.33-19.88 72.95 0 10.98 2.41 19.41 5.56 24.51l-26.48 17.44c-6.8-11.31-10.14-26.15-10.14-43.06 0-30.75 8.44-62.8 24.49-87.55 14.24-21.95 34.79-37.3 58.36-37.3z"
      />
    </svg>
  );
}

export function GoogleAdsLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cn("size-4", className)} aria-label="Google Ads" role="img">
      <path d="M9.2 3.6 2.6 15a3.3 3.3 0 1 0 5.7 3.3l6.6-11.4a3.3 3.3 0 1 0-5.7-3.3Z" fill="#FBBC04" />
      <path d="M14.8 3.6a3.3 3.3 0 0 1 4.5 1.2l2.1 3.7-5.7 3.3-2.1-3.7a3.3 3.3 0 0 1 1.2-4.5Z" fill="#4285F4" />
      <path d="m15.6 11.8 5.8-3.3 0 0-1 7.9a3.3 3.3 0 0 1-5.7 3.3l-2.1-3.6Z" fill="#4285F4" opacity=".0" />
      <path d="M21.4 15a3.3 3.3 0 1 1-5.7 3.3L13 13.6l5.7-3.3Z" fill="#4285F4" />
      <circle cx="5.5" cy="18.3" r="3.3" fill="#34A853" />
    </svg>
  );
}

export function TikTokLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cn("size-4", className)} aria-label="TikTok" role="img">
      <path d="M16.6 3c.4 2.2 1.7 3.6 3.9 3.8v3a7.2 7.2 0 0 1-3.9-1.2v6.3a5.6 5.6 0 1 1-5.6-5.6h.4v3.1a2.6 2.6 0 1 0 2.2 2.5V3h3Z" fill="#25F4EE" transform="translate(-0.6 -0.4)" />
      <path d="M16.6 3c.4 2.2 1.7 3.6 3.9 3.8v3a7.2 7.2 0 0 1-3.9-1.2v6.3a5.6 5.6 0 1 1-5.6-5.6h.4v3.1a2.6 2.6 0 1 0 2.2 2.5V3h3Z" fill="#FE2C55" transform="translate(0.6 0.4)" />
      <path d="M16.6 3c.4 2.2 1.7 3.6 3.9 3.8v3a7.2 7.2 0 0 1-3.9-1.2v6.3a5.6 5.6 0 1 1-5.6-5.6h.4v3.1a2.6 2.6 0 1 0 2.2 2.5V3h3Z" fill="#fff" />
    </svg>
  );
}

export function AppleLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cn("size-4", className)} aria-label="Apple" role="img" fill="currentColor">
      <path d="M16.4 12.6c0-2.4 2-3.6 2.1-3.7-1.2-1.7-3-1.9-3.6-2-1.5-.2-3 .9-3.8.9-.8 0-2-.9-3.3-.9-1.7 0-3.3 1-4.2 2.5-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.6 1.3-.1 1.8-.8 3.3-.8 1.6 0 2 .8 3.4.8 1.4 0 2.3-1.3 3.1-2.5 1-1.4 1.4-2.8 1.4-2.9 0 0-2.9-1.1-2.9-4.2ZM13.9 5.3c.7-.9 1.2-2 1-3.2-1 0-2.3.7-3 1.6-.7.8-1.2 2-1.1 3.1 1.2.1 2.3-.6 3.1-1.5Z" />
    </svg>
  );
}

export function SlackLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cn("size-4", className)} aria-label="Slack" role="img">
      <path d="M6 14.5a2 2 0 1 1-2-2h2v2Zm1 0a2 2 0 1 1 4 0v5a2 2 0 1 1-4 0v-5Z" fill="#E01E5A" />
      <path d="M9 6a2 2 0 1 1 2-2v2H9Zm0 1a2 2 0 1 1 0 4H4a2 2 0 1 1 0-4h5Z" fill="#36C5F0" />
      <path d="M18 9a2 2 0 1 1 2 2h-2V9Zm-1 0a2 2 0 1 1-4 0V4a2 2 0 1 1 4 0v5Z" fill="#2EB67D" />
      <path d="M15 18a2 2 0 1 1-2 2v-2h2Zm0-1a2 2 0 1 1 0-4h5a2 2 0 1 1 0 4h-5Z" fill="#ECB22E" />
    </svg>
  );
}

export function AppstackLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={cn("size-4", className)} aria-label="Appstack" role="img">
      <rect x="4" y="4" width="16" height="4.2" rx="1.6" fill="#8B7FF0" />
      <rect x="4" y="9.9" width="16" height="4.2" rx="1.6" fill="#5643E1" />
      <rect x="4" y="15.8" width="16" height="4.2" rx="1.6" fill="#3a2fa3" />
    </svg>
  );
}

export const PLATFORM_LABEL: Record<string, string> = {
  meta: "Meta",
  google_ads: "Google Ads",
  tiktok: "TikTok",
  apple_search_ads: "Apple Search Ads",
  appstack: "Appstack",
  slack: "Slack",
  email: "Email",
  telegram: "Telegram",
};

export function platformLabel(p: string | null | undefined): string {
  if (!p) return "—";
  return PLATFORM_LABEL[p] ?? PLATFORM_LABEL[p.toLowerCase()] ?? p;
}

/** Bare logo for a platform / provider key. */
export function PlatformLogo({ platform, className }: { platform: string | null | undefined; className?: string }) {
  switch ((platform ?? "").toLowerCase()) {
    case "meta":
    case "facebook":
    case "instagram":
    case "meta_ad_library":
      return <MetaLogo className={className} />;
    case "google_ads":
    case "google":
      return <GoogleAdsLogo className={className} />;
    case "tiktok":
      return <TikTokLogo className={className} />;
    case "apple_search_ads":
    case "asa":
    case "app_store":
      return <AppleLogo className={cn("text-fg", className)} />;
    case "appstack":
      return <AppstackLogo className={className} />;
    case "slack":
      return <SlackLogo className={className} />;
    case "email":
      return <Mail className={cn("size-4 text-fg", className)} strokeWidth={1.75} />;
    case "telegram":
      return <Send className={cn("size-4 text-[#2AABEE]", className)} strokeWidth={1.9} />;
    case "push":
      return <Bell className={cn("size-4", className)} />;
    default:
      return <Smartphone className={cn("size-4 text-muted", className)} strokeWidth={1.75} />;
  }
}

/** Logo inside a dark rounded tile (Connections cards, Ad Accounts rail). */
export function LogoTile({ platform, size = "md", className }: { platform: string; size?: "sm" | "md" | "lg"; className?: string }) {
  const dims = size === "sm" ? "size-7 rounded-lg" : size === "lg" ? "size-11 rounded-xl" : "size-9 rounded-[10px]";
  const logo = size === "lg" ? "scale-125" : "";
  return (
    <span className={cn("grid shrink-0 place-items-center border border-line bg-[#111113]", dims, className)}>
      <PlatformLogo platform={platform} className={logo} />
    </span>
  );
}
